export default String.raw`
$(document).ready(()=>{
    if ($('.promoBanner2').length > 0) {
        promoBanner2Functions.init();
    }
});

/**
 * Promotional Banner 2 Module
 * Handles promotional banner with swiper sliders and countdown timers
 * @module promoBanner2Functions
 */
const promoBanner2Functions = (() => {
    'use strict';
    
    const state = {
        items: [],
        timeUpdateInterval: null,
        initialized: false
    };
    
    const selectors = {
        container: '.promoBanner2',
        wrapper: '.promoBanner2__wrapper',
        item: '.promoBanner2__item',
        timer: '.timer',
        nextButton: '.swiper-button-next',
        prevButton: '.swiper-button-prev',
        countdownUnits: {
            days: '.countdown__time-unit--days',
            hours: '.countdown__time-unit--hours',
            minutes: '.countdown__time-unit--minutes',
            seconds: '.countdown__time-unit--seconds'
        }
    };
    
    /**
     * Safely parses settings JSON from banner item
     * @param {HTMLElement} item - The banner item element
     * @returns {Object|null} Parsed settings or null if invalid
     */
    const getSettings = (item) => {
        try {
            const settingsString = item.getAttribute('data-settings');
            if (settingsString) {
                return JSON.parse(settingsString);
            } else {
                return null;
            }
        } catch (e) {
            console.error('Error parsing banner settings:', e);
            return null;
        }
    };
    
    /**
     * Updates timer display with efficient DOM updates
     * @param {Date} currentTime - Current time
     * @param {Object} timer - Timer object with references and state
     */
    const updateTimer = (currentTime, timer) => {
        if (!timer?.element || !timer?.date) {
            return;
        }
        
        const timeLeft = timer.date - currentTime;
        
        if (timeLeft <= 0) {
            updateTimerDisplay(timer, 0, 0, 0, 0);
            return;
        }
        
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
        
        updateTimerDisplay(timer, days, hours, minutes, seconds);
        
        if (timer.element.style.visibility === 'hidden' || timer.element.style.opacity === '0') {
            timer.element.style.visibility = 'visible';
            timer.element.style.opacity = '1';
        }
    };
    
    /**
     * Updates timer display elements only if values have changed
     * @param {Object} timer - Timer object with references and state
     * @param {number} days - Days
     * @param {number} hours - Hours
     * @param {number} minutes - Minutes
     * @param {number} seconds - Seconds
     */
    const updateTimerDisplay = (timer, days, hours, minutes, seconds) => {
        const { refs, lastValues } = timer;
        
        if (lastValues.days !== days && refs.days) {
            refs.days.textContent = String(days).padStart(2, '0');
            lastValues.days = days;
        }
        
        if (lastValues.hours !== hours && refs.hours) {
            refs.hours.textContent = String(hours).padStart(2, '0');
            lastValues.hours = hours;
        }
        
        if (lastValues.minutes !== minutes && refs.minutes) {
            refs.minutes.textContent = String(minutes).padStart(2, '0');
            lastValues.minutes = minutes;
        }
        
        if (lastValues.seconds !== seconds && refs.seconds) {
            refs.seconds.textContent = String(seconds).padStart(2, '0');
            lastValues.seconds = seconds;
        }
    };
    
    /**
     * Sets up a single timer update interval for all timers
     */
    const setupTimerUpdates = () => {
        if (state.timeUpdateInterval) {
            clearInterval(state.timeUpdateInterval);
            state.timeUpdateInterval = null;
        }
        
        if (state.items.length > 0) {
            state.timeUpdateInterval = setInterval(() => {
                const now = new Date();
                
                state.items.forEach(item => {
                    if (item.timer?.element) {
                        const difference = item.timer.date - now;
                        
                        if (difference > 0) {
                            updateTimer(now, item.timer);
                        }
                        if (difference < 1 && (item.timer.element.style.visibility === 'hidden' || 
                            item.timer.element.style.opacity === '0')) {
                            item.timer.element.style.visibility = 'visible';
                            item.timer.element.style.opacity = '1';
                            updateTimer(new Date(item.timer.date.getTime() + 1), item.timer);
                        }
                    }
                });
            }, 1000);
        }
    };
    
    /**
     * Initializes the Swiper slider for a banner element
     * @param {HTMLElement} element - The slider container element
     * @returns {Swiper} The initialized Swiper instance
     */
    const initializeSlider = (element) => {
        try {
            return new Swiper(element, {
                loop: true,
                watchSlidesProgress: true,
                slidesPerView: 1,
                autoHeight: true,
                allowTouchMove: true,
                a11y: {
                    enabled: true,
                    prevSlideMessage: 'Previous slide',
                    nextSlideMessage: 'Next slide'
                },
                navigation: {
                    nextEl: element.querySelector(selectors.nextButton),
                    prevEl: element.querySelector(selectors.prevButton)
                },
                keyboard: {
                    enabled: true,
                    pageUpDown: true
                },
                on: {
                    init: function() {
                        app.manageTabIndexInsideSlide?.(this);
                    },
                    slideChangeTransitionEnd: function() {
                        app.manageTabIndexInsideSlide?.(this);
                    }
                }
            });
        } catch (error) {
            console.error('Error initializing Swiper slider:', error);
            return null;
        }
    };
    
    /**
     * Process banner items, extract settings and setup timers
     * @param {NodeList} items - List of banner item elements
     */
    const processBannerItems = (items) => {
        items.forEach(item => {
            const settings = getSettings(item);
            if (!settings) {
                return;
            }
            
            const timerElement = item.querySelector(selectors.timer);
            if (!timerElement || !settings.timerDateTo) {
                return;
            }
            
            let timerDate;
            try {
                timerDate = new Date(settings.timerDateTo);

                if (isNaN(timerDate.getTime())) {
                    return;
                }
            } catch(e) {
                console.warn('Invalid timer date:', settings.timerDateTo);
                return;
            }

            const timerRefs = {
                days: item.querySelector(selectors.countdownUnits.days),
                hours: item.querySelector(selectors.countdownUnits.hours),
                minutes: item.querySelector(selectors.countdownUnits.minutes),
                seconds: item.querySelector(selectors.countdownUnits.seconds)
            };
            
            state.items.push({
                item,
                settings,
                timer: {
                    element: timerElement,
                    date: timerDate,
                    refs: timerRefs,
                    lastValues: { days: null, hours: null, minutes: null, seconds: null }
                }
            });
        });
    };

    const init = () => {
        if (state.initialized) {
            return;
        }
        
        state.items = [];
        
        const banners = document.querySelectorAll(selectors.container);
        if (!banners.length) {
            return;
        }
        
        banners.forEach(banner => {
            const carousel = banner.querySelector(selectors.wrapper);
            if (!carousel) {
                return;
            }
            
            initializeSlider(carousel);

            const items = banner.querySelectorAll(selectors.item);
            if (items.length) {
                processBannerItems(items);
            }
        });
        
        setupTimerUpdates();
        state.initialized = true;
    };
    
    const destroy = () => {
        if (state.timeUpdateInterval) {
            clearInterval(state.timeUpdateInterval);
            state.timeUpdateInterval = null;
        }
        state.items = [];
        state.initialized = false;
    };
    
    $(document).ready(() => {
        if ($(selectors.container).length > 0) {
            init();
        }
    });
    
    return {
        init,
        destroy,
        updateTimer
    };
})();
`